import {
    Box, Button, Text, Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton, ModalBody, Flex, HStack, VStack, Grid, Link, Icon,
    IconButton, Skeleton, Alert, AlertIcon, AlertDescription, Spinner, useToast
} from '@chakra-ui/react';
import React, { useCallback, useEffect, useState } from 'react';
import {
    Package, User, MapPin, Truck, Wallet, Receipt, Clock, Pencil, ExternalLink, Download, RefreshCw, TriangleAlert, CircleCheck, FileText, Zap, Phone, Mail
} from 'lucide-react';
import ProdutoImagem from "../ProdutoImagem";
import GeradorEtiquetaMelhorEnvio from '../GeradorEtiquetaMelhorEnvio';
import { getDetalhesVenda, downloadArquivoProtegido } from "../../services/api";
import { isColetaMl } from "../../utils/envioMl";
import { formatPhone } from "../Crm/crmUtils";
import { STATUS_TAGA, STATUS_MARKETPLACE, MARCADORES, CANAIS, NUMEROS, ROTULO_SECAO, paraNumero, brl, formatarDocumento, Selo, BotaoCopiar, DicaSePuder, Canal, SeloOcorrencia, LinhaExtrato } from './ui';
import { SelosOcorrenciasItem, LinhasExtrato } from "../OcorrenciasItemVenda";

const FORMAS_PAGAMENTO = {
    PIX: 'Pix',
    CREDIT_CARD: 'Cartão de crédito',
    DEBIT_CARD: 'Cartão de débito',
    BOLETO: 'Boleto',
    OUTROS: 'Outros',
};

const ETAPAS = [
    { chave: 'venda', rotulo: 'Venda', sempre: true },
    { chave: 'confirmada', rotulo: 'Confirmada', sempre: true },
    { chave: 'para_enviar', rotulo: 'Para enviar', sempre: true },
    { chave: 'pronto_retirada', rotulo: 'Pronto p/ retirada' },
    { chave: 'em_transito', rotulo: 'Em trânsito', sempre: true },
    { chave: 'concluida', rotulo: 'Concluída', sempre: true },
    { chave: 'cancelada', rotulo: 'Cancelada', scheme: 'red' },
];

const dataHora = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    if (isNaN(d.getTime())) return null;
    return d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'America/Sao_Paulo' }).replace(',', '');
};

const formatarChave = (chave) => String(chave || '').replace(/\D/g, '').replace(/(\d{4})(?=\d)/g, '$1 ');

const formaPagamento = (forma) => {
    if (!forma || !String(forma).trim()) return null;
    return FORMAS_PAGAMENTO[String(forma).toUpperCase()] || forma;
};

const copiarTexto = (valor) => navigator.clipboard?.writeText(String(valor));

const temValor = (v) => v !== null && v !== undefined && String(v).trim() !== '';

const Secao = ({ titulo, icone, acao, children }) => (
    <Box px={5} py={4} borderTop="1px solid" borderColor="gray.100" _first={{ borderTop: 0 }}>
        <Flex align="center" justify="space-between" mb={2.5} minH="24px" gap={2}>
            <HStack spacing={1.5} color="gray.500">
                <Icon as={icone} boxSize="14px" strokeWidth={2.25} />
                <Text {...ROTULO_SECAO}>{titulo}</Text>
            </HStack>
            {acao}
        </Flex>
        {children}
    </Box>
);

const Linha = ({ rotulo, valor, mono, copiar, children }) => {
    if (!children && !temValor(valor)) return null;
    return (
        <Flex justify="space-between" align="baseline" gap={4} py={1.5}>
            <Text fontSize="12px" color="gray.500" flex="none">{rotulo}</Text>
            <HStack spacing={1} minW={0} justify="flex-end">
                {children || (
                    <Text fontSize="13px" fontWeight="600" color="gray.900" textAlign="right" wordBreak="break-word" fontFamily={mono ? "mono" : undefined} sx={NUMEROS}>{valor}</Text>
                )}
                {copiar && temValor(valor) && <BotaoCopiar valor={copiar === true ? valor : copiar} onCopiar={copiarTexto} label={`Copiar ${rotulo.toLowerCase()}`} tamanho="12px" />}
            </HStack>
        </Flex>
    );
};

const SemDados = ({ children }) => <Text fontSize="12px" color="gray.400">{children}</Text>;

const Indicador = ({ rotulo, valor, destaque, vazio }) => (
    <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="xl" px={4} py={3}>
        <Text {...ROTULO_SECAO} color="gray.500" mb={1}>{rotulo}</Text>
        <Text fontSize={destaque ? "20px" : "16px"} fontWeight={destaque ? "800" : "700"} color={vazio ? "gray.400" : "gray.900"} letterSpacing="-0.01em" noOfLines={1} sx={NUMEROS}>
            {valor}
        </Text>
    </Box>
);

const BotaoIconeSecao = ({ label, icone, ...props }) => (
    <DicaSePuder label={label}>
        <IconButton aria-label={label} icon={<Icon as={icone} boxSize="14px" />} size="sm" variant="ghost" color="gray.500" borderRadius="md" _hover={{ color: "gray.800", bg: "gray.100" }} {...props} />
    </DicaSePuder>
);

const DetalhesVendaModal = ({
    isOpen, onClose, pedido, isMobile, versao = 0,
    onEditarCliente, onEditarPrazo, onEmitirNota, emitindoNota, onImprimirEtiqueta, imprimindoEtiqueta, onConfirmarEntrega, acoes, renderSelosItem,
}) => {
    const toast = useToast();
    const [dados, setDados] = useState(null);
    const [erro, setErro] = useState(null);
    const [carregando, setCarregando] = useState(false);
    const [baixando, setBaixando] = useState(null);

    const carregar = useCallback(async () => {
        setCarregando(true);
        setErro(null);
        const res = await getDetalhesVenda(pedido.id_taga);
        if (res?.erro) {
            setErro(res.erro);
            setDados(null);
        } else {
            setDados(res);
        }
        setCarregando(false);
    }, [pedido.id_taga]);

    useEffect(() => {
        if (isOpen) carregar();
    }, [isOpen, versao, carregar]);

    const baixarNota = async (tipo) => {
        setBaixando(tipo);
        const numero = dados?.fiscal?.numero || pedido.id_taga;
        const res = await downloadArquivoProtegido(`/baixar_xml_ml?id=${pedido.id_taga}&tipo=${tipo}`, `nfe_${numero}.${tipo}`);
        if (res?.error) {
            toast({ title: tipo === 'pdf' ? 'Não foi possível baixar a DANFE' : 'Não foi possível baixar o XML', description: res.error, status: 'error', duration: 5000, isClosable: true });
        }
        setBaixando(null);
    };

    const venda = dados?.venda;
    const cliente = dados?.cliente;
    const endereco = dados?.endereco;
    const envio = dados?.envio;
    const etiqueta = dados?.etiqueta;
    const fiscal = dados?.fiscal;
    const pagamento = dados?.pagamento;

    const canal = CANAIS[pedido.origem];
    const statusTaga = STATUS_TAGA[pedido.status_taga];
    const statusMkt = STATUS_MARKETPLACE[pedido.status];
    const marcador = MARCADORES[pedido.marcador];
    const numeroPedido = pedido.origem === "TAGA" ? pedido.id_taga : pedido.id;
    const ehMl = pedido.origem === "Mercado Livre" || pedido.origem === "Mercado Shops";
    const ehTagaOuSite = pedido.origem === "TAGA" || pedido.origem === "ECOMMERCE";
    const isEntregaCombinar = pedido?.envio?.modo_envio === 'Entrega a Combinar';
    const produtos = pedido.produtos || [];

    const linhasEndereco = endereco
        ? [
            [endereco.endereco, endereco.numero].filter(Boolean).join(', '),
            [endereco.complemento, endereco.bairro].filter(Boolean).join(' · '),
            [endereco.cidade, endereco.uf].filter(Boolean).join(' / ') + (endereco.cep ? ` · CEP ${endereco.cep}` : ''),
        ].filter((l) => l && l.trim())
        : [];
    const linkMapa = endereco
        ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([endereco.endereco, endereco.numero, endereco.bairro, endereco.cidade, endereco.uf, endereco.cep].filter(Boolean).join(', '))}`
        : null;

    const codigoRastreio = envio?.codigo_rastreio || etiqueta?.codigo_rastreio || null;
    const linkRastreio = envio?.link_rastreio || etiqueta?.url_rastreio || (codigoRastreio ? `https://www.melhorrastreio.com.br/rastreio/${codigoRastreio}` : null);
    const etapas = ETAPAS.filter((e) => e.sempre || venda?.datas?.[e.chave]);
    const ultimaConcluida = etapas.reduce((acc, e, i) => (venda?.datas?.[e.chave] ? i : acc), -1);

    const total = pagamento?.total ?? paraNumero(pedido.total);
    const extrato = pedido.extrato;
    const frete = envio?.valor_frete;
    const semFrete = frete === null || frete === undefined;
    const forma = formaPagamento(pagamento?.forma);

    const corpo = () => (
        <Grid templateColumns={{ base: "1fr", lg: "minmax(0, 1fr) 360px" }} gap={4} alignItems="start">
            <VStack align="stretch" spacing={4} minW={0}>
                <Grid templateColumns={{ base: "repeat(2, 1fr)", md: "repeat(4, 1fr)" }} gap={3}>
                    <Indicador rotulo={extrato ? "Total realizado" : "Total"} valor={brl(extrato ? extrato.total_realizado : total)} destaque />
                    <Indicador rotulo="Unidades" valor={pedido.total_itens ?? produtos.reduce((s, p) => s + (paraNumero(p.quantidade) || 0), 0)} />
                    <Indicador rotulo="Frete" valor={semFrete ? 'Sem registro' : brl(frete)} vazio={semFrete} />
                    <Indicador rotulo="Pagamento" valor={forma || 'Sem registro'} vazio={!forma} />
                </Grid>

                <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="xl" overflow="hidden">
                    <HStack spacing={1.5} color="gray.500" px={5} pt={4} pb={2}>
                        <Icon as={Package} boxSize="14px" strokeWidth={2.25} />
                        <Text {...ROTULO_SECAO}>Produtos</Text>
                        <Box as="span" px={1.5} borderRadius="full" bg="gray.100" color="gray.600" fontSize="10px" fontWeight="700" sx={NUMEROS}>{produtos.length}</Box>
                    </HStack>
                    <Box px={5}>
                        {produtos.map((p, i) => {
                            const preco = paraNumero(p.preco);
                            const qtd = paraNumero(p.quantidade) || 0;
                            return (
                                <Grid key={p.item_venda_id ?? `${p.id}-${i}`} templateColumns="48px minmax(0,1fr) auto" columnGap={3} alignItems="center" py={3}
                                    borderTop={i === 0 ? 0 : "1px solid"} borderColor="gray.100">
                                    <Box borderRadius="lg" overflow="hidden" border="1px solid" borderColor="gray.100" w="48px" h="48px">
                                        <ProdutoImagem produto={p} origem="" width="46px" height="46px" />
                                    </Box>
                                    <Box minW={0}>
                                        <Text fontSize="13px" fontWeight="600" color="gray.900" lineHeight="1.35" sx={{ overflowWrap: "anywhere" }}>{p.nome}</Text>
                                        <Text fontSize="12px" color="gray.500" sx={NUMEROS}>
                                            SKU <Text as="span" fontFamily="mono" color="gray.700">{p.id}</Text>
                                            {p.codigo ? <> · Global <Text as="span" fontFamily="mono" color="gray.700">{p.codigo}</Text></> : null}
                                        </Text>
                                        {renderSelosItem ? renderSelosItem(p) : <SelosOcorrenciasItem pedido={pedido} produto={p} podeAgir={false} mostrarCancelar={false} Selo={SeloOcorrencia} desativarDica={isMobile} />}
                                    </Box>
                                    <Box textAlign="right" sx={NUMEROS}>
                                        <Text fontSize="13px" fontWeight="700" color="gray.900" whiteSpace="nowrap">{preco === null ? '-' : brl(preco * qtd)}</Text>
                                        <Text fontSize="11px" color="gray.500" whiteSpace="nowrap">{qtd} × {brl(preco)}</Text>
                                    </Box>
                                </Grid>
                            );
                        })}
                    </Box>
                    <Box borderTop="1px solid" borderColor="gray.100" bg="gray.50" px={5} py={3} fontSize="13px" sx={NUMEROS}>
                        {extrato ? (<>
                            <Box color="gray.600"><LinhasExtrato extrato={extrato} Linha={LinhaExtrato} /></Box>
                            <Flex justify="space-between" pt={1.5} mt={1} borderTop="1px solid" borderColor="gray.200" fontWeight="800" color="gray.900"><Text>Total realizado</Text><Text>{brl(extrato.total_realizado)}</Text></Flex>
                        </>) : (<>
                        <Flex justify="space-between" color="gray.600" py={0.5}><Text>Subtotal</Text><Text>{brl(pagamento?.subtotal ?? pedido.subtotal)}</Text></Flex>
                        <Flex justify="space-between" color="gray.600" py={0.5}><Text>Desconto</Text><Text color="red.500">{brl(pagamento?.desconto ?? pedido.desconto)}</Text></Flex>
                        <Flex justify="space-between" pt={1.5} mt={1} borderTop="1px solid" borderColor="gray.200" fontWeight="800" color="gray.900"><Text>Total</Text><Text>{brl(total)}</Text></Flex>
                        </>)}
                    </Box>
                </Box>

                <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="xl" px={5} py={4}>
                    <Flex align="center" justify="space-between" mb={4} gap={3} wrap="wrap">
                        <HStack spacing={1.5} color="gray.500">
                            <Icon as={Clock} boxSize="14px" strokeWidth={2.25} />
                            <Text {...ROTULO_SECAO}>Linha do tempo</Text>
                        </HStack>
                        {envio?.data_limite_envio && (
                            <HStack spacing={2}>
                                <Text fontSize="12px" color="gray.500">Prazo de envio</Text>
                                <Text fontSize="13px" fontWeight="700" color="gray.900" sx={NUMEROS}>{dataHora(envio.data_limite_envio)}</Text>
                                {envio.prazo_editado_manualmente && <Selo scheme="purple" h="18px" fontSize="10px">manual</Selo>}
                                {onEditarPrazo && <BotaoIconeSecao label="Editar prazo de envio" icone={Pencil} onClick={onEditarPrazo} />}
                            </HStack>
                        )}
                    </Flex>
                    <Grid templateColumns={{ base: "1fr", md: `repeat(${etapas.length}, minmax(0, 1fr))` }} gap={{ base: 3, md: 0 }}>
                        {etapas.map((e, i) => {
                            const data = venda?.datas?.[e.chave];
                            const feita = !!data;
                            const cor = e.scheme || 'green';
                            return (
                                <Flex key={e.chave} direction={{ base: "row", md: "column" }} align={{ base: "center", md: "flex-start" }} gap={{ base: 3, md: 2 }}>
                                    <Flex align="center" w={{ md: "100%" }}>
                                        <Box w="12px" h="12px" borderRadius="full" flex="none" bg={feita ? `${cor}.500` : "white"} border="2px solid" borderColor={feita ? `${cor}.500` : "gray.300"}
                                            boxShadow={feita ? `0 0 0 3px var(--chakra-colors-${cor}-100)` : "none"} />
                                        {i < etapas.length - 1 && <Box display={{ base: "none", md: "block" }} flex="1" h="2px" mx={1} bg={i < ultimaConcluida ? "green.400" : "gray.200"} />}
                                    </Flex>
                                    <Box pr={2}>
                                        <Text fontSize="12px" fontWeight="600" color={feita ? "gray.900" : "gray.400"}>{e.rotulo}</Text>
                                        <Text fontSize="11px" color={feita ? "gray.500" : "gray.300"} sx={NUMEROS}>{feita ? dataHora(data) : 'Aguardando'}</Text>
                                    </Box>
                                </Flex>
                            );
                        })}
                    </Grid>
                </Box>
            </VStack>

            <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="xl" overflow="hidden">
                <Secao titulo="Cliente" icone={User}
                    acao={onEditarCliente && <BotaoIconeSecao label="Editar cliente" icone={Pencil} onClick={onEditarCliente} />}>
                    {cliente ? (
                        <>
                            <Text fontSize="14px" fontWeight="700" color="gray.900" lineHeight="1.3">{cliente.nome || cliente.nickname}</Text>
                            {cliente.nome && cliente.nickname && cliente.nickname !== cliente.nome && <Text fontSize="12px" color="gray.500">{cliente.nickname}</Text>}
                            <Box mt={2}>
                                <Linha rotulo={cliente.tipo === 'PJ' ? 'CNPJ' : 'CPF'} valor={formatarDocumento(cliente.documento)} mono copiar={cliente.documento} />
                                {cliente.tipo === 'PJ' && <Linha rotulo="Inscrição estadual" valor={cliente.ie} mono />}
                                {temValor(cliente.telefone) && (
                                    <Linha rotulo="Telefone">
                                        <Link href={`tel:${String(cliente.telefone).replace(/\D/g, '')}`} fontSize="13px" fontWeight="600" color="gray.900" display="inline-flex" alignItems="center" gap={1} sx={NUMEROS}>
                                            <Icon as={Phone} boxSize="12px" color="gray.400" />{formatPhone(cliente.telefone)}
                                        </Link>
                                        <BotaoCopiar valor={cliente.telefone} onCopiar={copiarTexto} label="Copiar telefone" tamanho="12px" />
                                    </Linha>
                                )}
                                {temValor(cliente.email) && (
                                    <Linha rotulo="E-mail">
                                        <Link href={`mailto:${cliente.email}`} fontSize="13px" fontWeight="600" color="gray.900" wordBreak="break-all" textAlign="right" display="inline-flex" alignItems="center" gap={1}>
                                            <Icon as={Mail} boxSize="12px" color="gray.400" flex="none" />{cliente.email}
                                        </Link>
                                        <BotaoCopiar valor={cliente.email} onCopiar={copiarTexto} label="Copiar e-mail" tamanho="12px" />
                                    </Linha>
                                )}
                            </Box>
                        </>
                    ) : <SemDados>Cliente não registrado</SemDados>}
                </Secao>

                <Secao titulo="Endereço" icone={MapPin}
                    acao={linkMapa && <DicaSePuder label="Ver no mapa"><IconButton as={Link} href={linkMapa} isExternal aria-label="Ver no mapa" icon={<Icon as={ExternalLink} boxSize="14px" />} size="sm" variant="ghost" color="gray.500" borderRadius="md" /></DicaSePuder>}>
                    {linhasEndereco.length ? (
                        <VStack align="stretch" spacing={0.5}>
                            {linhasEndereco.map((l, i) => <Text key={i} fontSize="13px" color={i === 0 ? "gray.900" : "gray.600"} fontWeight={i === 0 ? "600" : "400"} sx={NUMEROS}>{l}</Text>)}
                        </VStack>
                    ) : <SemDados>Endereço ainda não registrado</SemDados>}
                </Secao>

                <Secao titulo="Nota fiscal" icone={Receipt}
                    acao={fiscal?.emitida ? (
                        <HStack spacing={0}>
                            <BotaoIconeSecao label="Baixar DANFE" icone={Download} isLoading={baixando === 'pdf'} onClick={() => baixarNota('pdf')} />
                            <Button size="sm" variant="ghost" color="gray.500" fontSize="11px" fontWeight="700" px={2} borderRadius="md" isLoading={baixando === 'xml'} onClick={() => baixarNota('xml')}>XML</Button>
                        </HStack>
                    ) : null}>
                    {fiscal?.emitida ? (
                        <>
                            <Selo scheme="green" icone={CircleCheck}>Autorizada</Selo>
                            <Box mt={2}>
                                <Linha rotulo="Número" valor={fiscal.numero} mono />
                                <Linha rotulo="Emissão" valor={dataHora(fiscal.data_emissao)} />
                                <Linha rotulo="Emissor" valor={fiscal.emissor === 'mercado_livre' ? 'Mercado Livre' : 'TAGA'} />
                            </Box>
                            {temValor(fiscal.chave) && (
                                <Box mt={2} p={2.5} bg="gray.50" borderRadius="md">
                                    <Flex justify="space-between" align="center" mb={0.5}>
                                        <Text fontSize="11px" color="gray.500">Chave de acesso</Text>
                                        <BotaoCopiar valor={String(fiscal.chave).replace(/\D/g, '')} onCopiar={copiarTexto} label="Copiar chave de acesso" tamanho="12px" />
                                    </Flex>
                                    <Text fontSize="12px" fontFamily="mono" color="gray.800" lineHeight="1.5" wordBreak="break-word">{formatarChave(fiscal.chave)}</Text>
                                </Box>
                            )}
                        </>
                    ) : (
                        <Flex align="center" justify="space-between" gap={3} wrap="wrap">
                            <Box>
                                <Selo scheme={fiscal ? "red" : "yellow"} icone={TriangleAlert}>{fiscal ? 'Não autorizada' : 'Pendente'}</Selo>
                                {fiscal?.motivo_erro && <Text mt={1.5} fontSize="12px" color="red.600">{fiscal.motivo_erro}</Text>}
                            </Box>
                            {onEmitirNota && (
                                <Button size="sm" colorScheme="orange" borderRadius="md" leftIcon={emitindoNota ? <Spinner size="xs" /> : <Icon as={FileText} boxSize="14px" />}
                                    isDisabled={emitindoNota} onClick={onEmitirNota}>Emitir NF-e</Button>
                            )}
                        </Flex>
                    )}
                </Secao>

                <Secao titulo="Envio" icone={Truck}
                    acao={(isEntregaCombinar && onConfirmarEntrega) ? <Button size="xs" variant="outline" colorScheme="orange" borderRadius="md" onClick={onConfirmarEntrega}>Confirmar dados</Button> : null}>
                    {envio ? (
                        <>
                            <Text fontSize="14px" fontWeight="700" color={isEntregaCombinar ? "red.600" : "gray.900"} lineHeight="1.3">{envio.modo_envio || 'Modalidade não registrada'}</Text>
                            {(envio.empresa || venda?.transportadora_combinada) && <Text fontSize="12px" color="gray.500">{envio.empresa || venda?.transportadora_combinada}</Text>}
                            {(envio.flex || isColetaMl(pedido.envio)) && (
                                <HStack spacing={1.5} mt={2}>
                                    {envio.flex && <Selo scheme="green" icone={Zap}>Flex</Selo>}
                                    {isColetaMl(pedido.envio) && <Selo scheme="pink" icone={Truck}>Coleta na TAGA</Selo>}
                                </HStack>
                            )}
                            <Box mt={2}>
                                {!semFrete && <Linha rotulo="Frete" valor={brl(frete)} />}
                                <Linha rotulo="Rastreio" valor={codigoRastreio} mono copiar />
                                {etiqueta && (
                                    <>
                                        <Linha rotulo="Etiqueta paga" valor={dataHora(etiqueta.paga)} />
                                        <Linha rotulo="Etiqueta gerada" valor={dataHora(etiqueta.gerada)} />
                                        <Linha rotulo="Postada" valor={dataHora(etiqueta.postada)} />
                                        <Linha rotulo="Entregue" valor={dataHora(etiqueta.entregue)} />
                                        {etiqueta.preco !== null && <Linha rotulo="Custo da etiqueta" valor={brl(etiqueta.preco)} />}
                                    </>
                                )}
                                <Linha rotulo="Observação" valor={envio.observacao} />
                            </Box>
                            <Flex wrap="wrap" gap={2} mt={3}>
                                {linkRastreio && (
                                    <Button as={Link} href={linkRastreio} isExternal size="sm" variant="outline" borderRadius="md" rightIcon={<Icon as={ExternalLink} boxSize="13px" />}
                                        _hover={{ textDecoration: "none", bg: "gray.50" }}>Acompanhar</Button>
                                )}
                                {ehMl && onImprimirEtiqueta && (imprimindoEtiqueta ? <Spinner size="sm" color="orange.400" /> : (
                                    <>
                                        <Button size="sm" variant="outline" borderRadius="md" leftIcon={<Icon as={Download} boxSize="14px" />} onClick={() => onImprimirEtiqueta('pdf')}>Etiqueta PDF</Button>
                                        <Button size="sm" variant="outline" borderRadius="md" leftIcon={<Icon as={Download} boxSize="14px" />} onClick={() => onImprimirEtiqueta('zpl2')}>ZPL</Button>
                                    </>
                                ))}
                                {(ehTagaOuSite || isEntregaCombinar) && <GeradorEtiquetaMelhorEnvio venda={pedido} />}
                            </Flex>
                        </>
                    ) : <SemDados>Envio não registrado</SemDados>}
                </Secao>

                <Secao titulo="Pagamento" icone={Wallet}>
                    <Linha rotulo="Forma" valor={forma} />
                    <Linha rotulo="Vendedor" valor={venda?.vendedor?.name} />
                    <Linha rotulo="Cupom" valor={venda?.cupom} mono />
                    {ehMl && <Linha rotulo="Conta" valor={venda?.conta ? `Conta ${venda.conta}` : null} />}
                    <Linha rotulo="Observação" valor={pagamento?.observacao} />
                    {!forma && !venda?.vendedor && !venda?.cupom && !pagamento?.observacao && !(ehMl && venda?.conta) && <SemDados>Sem detalhes de pagamento</SemDados>}
                </Secao>
            </Box>
        </Grid>
    );

    return (
        <Modal isOpen={isOpen} onClose={onClose} size={{ base: "full", md: "6xl" }} scrollBehavior="inside" isCentered>
            <ModalOverlay bg="blackAlpha.600" />
            <ModalContent borderRadius={{ base: 0, md: "2xl" }} overflow="hidden" bg="gray.50" h={{ base: "100dvh", md: "auto" }} maxH={{ base: "100dvh", md: "90vh" }}>
                <ModalHeader bg="white" borderBottom="1px solid" borderColor="gray.200" px={{ base: 4, md: 6 }} py={4}>
                    <Flex align="center" gap={2.5} pr={10} wrap="wrap">
                        <Canal pedido={pedido} isMobile={isMobile} />
                        <Text fontSize={{ base: "16px", md: "18px" }} fontWeight="700" color="gray.900" letterSpacing="-0.01em" sx={NUMEROS}>#{numeroPedido}</Text>
                        <BotaoCopiar valor={numeroPedido} onCopiar={copiarTexto} label="Copiar número do pedido" />
                        {acoes && <Box ml={{ base: 0, md: "auto" }} w={{ base: "100%", md: "auto" }}>{acoes}</Box>}
                    </Flex>
                    <Flex wrap="wrap" gap={1.5} mt={2.5}>
                        {statusTaga && <Selo scheme={statusTaga.scheme} forte ponto>TAGA · {statusTaga.texto}</Selo>}
                        {statusMkt && <Selo scheme={statusMkt.scheme} icone={statusMkt.icone}>{canal?.sigla === 'SITE' ? 'Site' : (canal?.sigla || 'Canal')} · {statusMkt.texto}</Selo>}
                        {marcador && <Selo scheme={marcador.scheme} forte icone={marcador.icone}>{marcador.texto}</Selo>}
                        {pedido.pack_id && <Selo scheme="blue">Pack #{pedido.pack_id}</Selo>}
                        <Selo scheme="gray" icone={Clock}>{pedido.data_hora}</Selo>
                    </Flex>
                </ModalHeader>
                <ModalCloseButton top={4} right={4} borderRadius="lg" />
                <ModalBody px={{ base: 3, md: 6 }} py={{ base: 4, md: 5 }}>
                    {carregando && !dados ? (
                        <Grid templateColumns={{ base: "1fr", lg: "minmax(0, 1fr) 360px" }} gap={4}>
                            <VStack align="stretch" spacing={4}>
                                <Grid templateColumns={{ base: "repeat(2, 1fr)", md: "repeat(4, 1fr)" }} gap={3}>
                                    {[0, 1, 2, 3].map((i) => <Skeleton key={i} h="70px" borderRadius="xl" />)}
                                </Grid>
                                <Skeleton h="220px" borderRadius="xl" />
                                <Skeleton h="110px" borderRadius="xl" />
                            </VStack>
                            <Skeleton h="420px" borderRadius="xl" />
                        </Grid>
                    ) : erro ? (
                        <Alert status="error" borderRadius="xl" alignItems="center">
                            <AlertIcon />
                            <AlertDescription fontSize="sm" flex="1">{erro}</AlertDescription>
                            <Button size="sm" variant="outline" colorScheme="red" leftIcon={<Icon as={RefreshCw} boxSize="13px" />} onClick={carregar}>Tentar de novo</Button>
                        </Alert>
                    ) : dados ? corpo() : null}
                </ModalBody>
            </ModalContent>
        </Modal>
    );
};

export default DetalhesVendaModal;
